import { useConfirm } from '@/components/Confirm';
import {
  type CreateVaccineBody,
  type UpdateVaccineBody,
  type Vaccine,
  createVaccine,
  deactivateVaccine,
  deleteVaccinePermanently,
  listVaccines,
  loadStandardVaccineLibrary,
  updateVaccine,
} from '@/lib/api';
import { Pencil, Plus, RefreshCw, Syringe, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

type SpeciesValue = 'dog' | 'cat';

function parseSpecies(raw: string): SpeciesValue[] {
  try {
    const arr = JSON.parse(raw) as unknown;
    if (Array.isArray(arr)) return arr as SpeciesValue[];
  } catch {
    // fallback
  }
  return [];
}

function speciesLabel(raw: string): string {
  const species = parseSpecies(raw);
  if (species.length === 0) return '—';
  return species.map((s) => (s === 'dog' ? 'Dog' : 'Cat')).join(' / ');
}

interface VaccineFormState {
  name: string;
  slug: string;
  speciesDog: boolean;
  speciesCat: boolean;
  description: string;
  typicalValidityMonths: string;
  isCore: boolean;
}

const EMPTY_FORM: VaccineFormState = {
  name: '',
  slug: '',
  speciesDog: true,
  speciesCat: false,
  description: '',
  typicalValidityMonths: '',
  isCore: false,
};

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s_-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 60);
}

interface VaccineFormProps {
  initial?: VaccineFormState;
  onSave: (form: VaccineFormState) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
}

function VaccineForm({
  initial = EMPTY_FORM,
  onSave,
  onCancel,
  submitLabel = 'Save',
}: VaccineFormProps) {
  const [form, setForm] = useState<VaccineFormState>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof VaccineFormState>(key: K, value: VaccineFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!form.slug.trim()) {
      setError('Slug is required');
      return;
    }
    if (!form.speciesDog && !form.speciesCat) {
      setError('At least one species must be selected');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
    } catch (err) {
      setError((err as Error).message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label htmlFor="vax-name" className="block text-sm font-medium">
            Name <span className="text-destructive">*</span>
          </label>
          <input
            id="vax-name"
            type="text"
            required
            className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            value={form.name}
            onChange={(e) => {
              set('name', e.target.value);
              if (!initial.slug) set('slug', slugify(e.target.value));
            }}
            placeholder="e.g. Bordetella (Kennel Cough)"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="vax-slug" className="block text-sm font-medium">
            Slug <span className="text-destructive">*</span>
          </label>
          <input
            id="vax-slug"
            type="text"
            required
            className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
            value={form.slug}
            onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
            placeholder="e.g. bordetella"
          />
          <p className="text-[11px] text-muted-foreground">
            Lowercase letters, numbers, hyphens and underscores only. Cannot be changed after
            creation.
          </p>
        </div>
      </div>

      <div className="space-y-1">
        <span className="block text-sm font-medium">Species</span>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.speciesDog}
              onChange={(e) => set('speciesDog', e.target.checked)}
            />
            Dog
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.speciesCat}
              onChange={(e) => set('speciesCat', e.target.checked)}
            />
            Cat
          </label>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label htmlFor="vax-validity" className="block text-sm font-medium">
            Typical validity (months)
          </label>
          <input
            id="vax-validity"
            type="number"
            min={1}
            className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            value={form.typicalValidityMonths}
            onChange={(e) => set('typicalValidityMonths', e.target.value)}
            placeholder="e.g. 12"
          />
        </div>

        <div className="flex items-center gap-2 pt-5">
          <input
            id="vax-core"
            type="checkbox"
            checked={form.isCore}
            onChange={(e) => set('isCore', e.target.checked)}
          />
          <label htmlFor="vax-core" className="text-sm font-medium cursor-pointer">
            Core vaccine (WSAVA recommended)
          </label>
        </div>
      </div>

      <div className="space-y-1">
        <label htmlFor="vax-description" className="block text-sm font-medium">
          Description
        </label>
        <textarea
          id="vax-description"
          rows={2}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm resize-none"
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Optional notes shown to staff"
        />
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border px-4 py-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function vaccineToForm(v: Vaccine): VaccineFormState {
  const species = parseSpecies(v.speciesAllowed);
  return {
    name: v.name,
    slug: v.slug,
    speciesDog: species.includes('dog'),
    speciesCat: species.includes('cat'),
    description: v.description ?? '',
    typicalValidityMonths: v.typicalValidityMonths?.toString() ?? '',
    isCore: v.isCore === 1,
  };
}

function formToCreate(form: VaccineFormState): CreateVaccineBody {
  const speciesAllowed: SpeciesValue[] = [];
  if (form.speciesDog) speciesAllowed.push('dog');
  if (form.speciesCat) speciesAllowed.push('cat');
  return {
    name: form.name.trim(),
    slug: form.slug.trim(),
    speciesAllowed,
    description: form.description.trim() || null,
    typicalValidityMonths: form.typicalValidityMonths ? Number(form.typicalValidityMonths) : null,
    isCore: form.isCore,
  };
}

function formToUpdate(form: VaccineFormState): UpdateVaccineBody {
  const speciesAllowed: SpeciesValue[] = [];
  if (form.speciesDog) speciesAllowed.push('dog');
  if (form.speciesCat) speciesAllowed.push('cat');
  return {
    name: form.name.trim(),
    speciesAllowed,
    description: form.description.trim() || null,
    typicalValidityMonths: form.typicalValidityMonths ? Number(form.typicalValidityMonths) : null,
    isCore: form.isCore,
  };
}

/**
 * Vaccine catalogue settings page.
 *
 * Lists all active vaccines for the tenant with edit/deactivate actions.
 * "Add vaccine" opens an inline form. "Load standard library" seeds the
 * common UK set for tenants starting from scratch.
 */
export function VaccinesSettings() {
  const [vaccines, setVaccines] = useState<Vaccine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listVaccines(false); // include inactive for full management
      setVaccines(res.vaccines);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load vaccines');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(form: VaccineFormState) {
    await createVaccine(formToCreate(form));
    setAdding(false);
    await load();
  }

  async function handleUpdate(id: string, form: VaccineFormState) {
    await updateVaccine(id, formToUpdate(form));
    setEditingId(null);
    await load();
  }

  async function handleDeactivate(v: Vaccine) {
    const yes = await confirm({
      title: 'Deactivate vaccine?',
      message: `"${v.name}" will no longer appear in pickers or the policy editor. Existing pet vaccination records are not affected. You can delete it permanently afterwards.`,
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (!yes) return;
    try {
      await deactivateVaccine(v.id);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleDeletePermanently(v: Vaccine) {
    const yes = await confirm({
      title: `Delete "${v.name}" permanently?`,
      message:
        'This will permanently remove the vaccine and any associated vaccine policies. This cannot be undone.',
      confirmLabel: 'Delete permanently',
      danger: true,
    });
    if (!yes) return;
    try {
      await deleteVaccinePermanently(v.id);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleLoadLibrary() {
    const yes = await confirm({
      title: 'Load standard vaccine library?',
      message:
        'This will add common UK vaccines (DHPP, Leptospirosis, Bordetella, etc.) to your catalogue. Vaccines you have already configured are not affected.',
      confirmLabel: 'Load library',
      danger: false,
    });
    if (!yes) return;
    setSeeding(true);
    setError(null);
    try {
      const result = await loadStandardVaccineLibrary();
      if (result.inserted === 0 && result.policiesInserted === 0) {
        setError('All standard vaccines are already in your catalogue.');
      } else {
        setNotice(
          `${result.inserted} vaccine${result.inserted === 1 ? '' : 's'} added and ${
            result.policiesInserted
          } default polic${result.policiesInserted === 1 ? 'y' : 'ies'} written for dogs and cats — review them under Vaccine policies.`,
        );
      }
      await load();
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load library');
    } finally {
      setSeeding(false);
    }
  }

  const activeVaccines = vaccines.filter((v) => v.isActive === 1);
  const inactiveVaccines = vaccines.filter((v) => v.isActive !== 1);
  const hasNoCore = activeVaccines.filter((v) => v.isCore === 1).length === 0;

  return (
    <div className="space-y-6">
      <ConfirmDialog />

      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Vaccines</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your vaccine catalogue. Vaccines defined here are used in the vaccine policy
            editor, pet vaccination records, and the check-in gate.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {(activeVaccines.length === 0 || hasNoCore) && !adding && (
            <button
              type="button"
              disabled={seeding}
              onClick={handleLoadLibrary}
              className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-60"
            >
              <RefreshCw className="size-3.5" strokeWidth={1.75} />
              {seeding ? 'Loading…' : 'Load standard library'}
            </button>
          )}
          {!adding && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
            >
              <Plus className="size-3.5" />
              Add vaccine
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="flex items-start justify-between gap-3 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
            <X className="size-4" />
          </button>
        </div>
      )}
      {notice && (
        <div
          className="flex items-start justify-between gap-3 rounded-lg bg-success/10 px-4 py-3 text-sm text-success"
          role="status"
        >
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss notice">
            <X className="size-4" />
          </button>
        </div>
      )}

      {adding && (
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="mb-4 font-medium">Add vaccine</h2>
          <VaccineForm
            onSave={handleCreate}
            onCancel={() => setAdding(false)}
            submitLabel="Create vaccine"
          />
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : activeVaccines.length === 0 && !adding ? (
        <div className="rounded-xl border border-dashed border-border py-12 text-center">
          <Syringe className="mx-auto mb-3 size-8 text-muted-foreground/40" strokeWidth={1.25} />
          <p className="text-sm font-medium text-muted-foreground">No vaccines configured</p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            Add individual vaccines or load the standard library to get started.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="min-w-[560px] w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Name</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Species</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground hidden sm:table-cell">
                  Validity
                </th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground hidden md:table-cell">
                  Type
                </th>
                <th className="w-28 px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {activeVaccines.map((v) => (
                <>
                  <tr key={v.id} className={editingId === v.id ? 'bg-secondary/20' : undefined}>
                    <td className="px-4 py-3">
                      <span className="font-medium">{v.name}</span>
                      {v.description && (
                        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1">
                          {v.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {speciesLabel(v.speciesAllowed)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                      {v.typicalValidityMonths ? (
                        <span className="num">{v.typicalValidityMonths} mo</span>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {v.isCore === 1 ? (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          Core
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/60">Non-core</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setEditingId(editingId === v.id ? null : v.id)}
                          aria-label={editingId === v.id ? 'Cancel edit' : 'Edit'}
                          className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                        >
                          <Pencil className="size-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDeactivate(v)}
                          aria-label="Deactivate"
                          title="Deactivate"
                          className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {editingId === v.id && (
                    <tr key={`${v.id}-edit`}>
                      <td colSpan={5} className="bg-secondary/10 px-5 py-4">
                        <VaccineForm
                          initial={vaccineToForm(v)}
                          onSave={(form) => handleUpdate(v.id, form)}
                          onCancel={() => setEditingId(null)}
                          submitLabel="Update vaccine"
                        />
                      </td>
                    </tr>
                  )}
                </>
              ))}
              {inactiveVaccines.map((v) => (
                <tr key={v.id} className="opacity-50">
                  <td className="px-4 py-3">
                    <span className="font-medium line-through">{v.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">(inactive)</span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {speciesLabel(v.speciesAllowed)}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell" />
                  <td className="px-4 py-3 hidden md:table-cell" />
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => void handleDeletePermanently(v)}
                        aria-label="Delete permanently"
                        title="Delete permanently"
                        className="grid size-7 place-items-center rounded-md text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
